"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.NEW_THREAD_PRIORITY = void 0;
exports.createThread = createThread;
exports.ensureThreadManifest = ensureThreadManifest;
exports.setThreadPriority = setThreadPriority;
exports.setThreadDeps = setThreadDeps;
const path = __importStar(require("path"));
const dist_1 = require("../../core/dist");
/** Authored baseline priority for a new manifest — mid-range, leaving slack to drag either way. */
exports.NEW_THREAD_PRIORITY = 1000;
const DEFAULT_MANIFEST_BODY = 'Thread manifest — authored roadmap metadata only (`priority` + `depends_on`). ' +
    'The thread\'s roadmap status and history are *derived* (`buildRoadmap`), never stored here.';
function manifestPathFor(loomRoot, weaveId, threadId) {
    return path.join(loomRoot, 'loom', weaveId, threadId, 'thread.md');
}
async function createThread(input, deps) {
    const loomRoot = deps.getActiveLoomRoot();
    const threadPath = path.join(loomRoot, 'loom', input.weaveId, input.threadId);
    await deps.fs.ensureDir(threadPath);
    const filePath = manifestPathFor(loomRoot, input.weaveId, input.threadId);
    if (await deps.fs.pathExists(filePath)) {
        throw new Error(`A thread.md already exists for ${input.weaveId}/${input.threadId}. ` +
            `Use setThreadPriority / setThreadDeps to update it.`);
    }
    const id = (0, dist_1.generateDocId)('thread');
    const frontmatter = (0, dist_1.createBaseFrontmatter)('thread', id, input.title ?? input.threadId, null);
    const doc = {
        ...frontmatter,
        status: 'active',
        priority: input.priority ?? exports.NEW_THREAD_PRIORITY,
        depends_on: input.dependsOn ?? [],
        content: DEFAULT_MANIFEST_BODY,
    };
    await deps.saveDoc(doc, filePath);
    return { id, filePath };
}
/**
 * Auto-scaffold seam: ensure the thread has a `thread.md`, creating one only if
 * absent. Idempotent and safe to call from every doc-create use-case so that the
 * first `loom_create_*` into a brand-new thread materialises its manifest — the
 * invariant "every thread has a manifest" that keeps the roadmap total.
 */
async function ensureThreadManifest(weaveId, threadId, title, deps) {
    const loomRoot = deps.getActiveLoomRoot();
    if (await deps.fs.pathExists(manifestPathFor(loomRoot, weaveId, threadId)))
        return;
    await createThread({ weaveId, threadId, title }, deps);
}
/** Walk every `loom/{weave}/{thread}/thread.md` (live + archived). Best-effort. */
async function scanManifests(loomRoot, deps) {
    const out = [];
    const roots = [
        { base: path.join(loomRoot, 'loom'), archived: false },
        { base: path.join(loomRoot, 'loom', '.archive'), archived: true },
    ];
    for (const { base, archived } of roots) {
        if (!(await deps.fs.pathExists(base)))
            continue;
        const weaves = await deps.fs.readdir(base).catch(() => []);
        for (const w of weaves) {
            if (w === '.archive')
                continue;
            const wp = path.join(base, w);
            const wstat = await deps.fs.stat(wp).catch(() => null);
            if (!wstat?.isDirectory())
                continue;
            const threads = await deps.fs.readdir(wp).catch(() => []);
            for (const t of threads) {
                const mp = path.join(wp, t, 'thread.md');
                if (!(await deps.fs.pathExists(mp)))
                    continue;
                try {
                    const d = (await deps.loadDoc(mp));
                    out.push({ weaveId: w, threadId: t, ulid: d.id, dependsOn: d.depends_on ?? [], filePath: mp, archived });
                }
                catch {
                    // skip malformed manifest
                }
            }
        }
    }
    return out;
}
/** White/grey/black DFS — true if the dependency graph contains any cycle. */
function hasCycle(graph) {
    const WHITE = 0, GREY = 1, BLACK = 2;
    const color = new Map();
    for (const k of graph.keys())
        color.set(k, WHITE);
    const dfs = (u) => {
        color.set(u, GREY);
        for (const v of graph.get(u) ?? []) {
            const c = color.get(v);
            if (c === undefined)
                continue; // external/unknown target — not part of the graph
            if (c === GREY)
                return true;
            if (c === WHITE && dfs(v))
                return true;
        }
        color.set(u, BLACK);
        return false;
    };
    for (const k of graph.keys())
        if (color.get(k) === WHITE && dfs(k))
            return true;
    return false;
}
async function setThreadPriority(input, deps) {
    const loomRoot = deps.getActiveLoomRoot();
    const all = await scanManifests(loomRoot, deps);
    const target = all.find(m => m.ulid === input.threadUlid && !m.archived);
    if (!target)
        throw new Error(`No live thread.md with id ${input.threadUlid}.`);
    const doc = (await deps.loadDoc(target.filePath));
    const updated = { ...doc, priority: input.priority, updated: (0, dist_1.today)() };
    await deps.saveDoc(updated, target.filePath);
    return { id: doc.id, filePath: target.filePath, priority: input.priority };
}
async function setThreadDeps(input, deps) {
    const loomRoot = deps.getActiveLoomRoot();
    const all = await scanManifests(loomRoot, deps);
    const target = all.find(m => m.ulid === input.threadUlid && !m.archived);
    if (!target)
        throw new Error(`No live thread.md with id ${input.threadUlid}.`);
    // Existence: every dependency must resolve to a known manifest (live or archived).
    const known = new Set(all.map(m => m.ulid));
    const missing = input.dependsOn.filter(d => !known.has(d));
    if (missing.length > 0) {
        throw new Error(`Write refused: depends_on references unknown thread(s): ${missing.join(', ')}.`);
    }
    if (input.dependsOn.includes(input.threadUlid)) {
        throw new Error('Write refused: a thread cannot depend on itself.');
    }
    // Cycle: build the graph with the proposed change and refuse if it introduces one.
    const graph = new Map();
    for (const m of all) {
        graph.set(m.ulid, m.ulid === input.threadUlid ? [...input.dependsOn] : [...m.dependsOn]);
    }
    if (hasCycle(graph)) {
        throw new Error('Write refused: these dependencies would introduce a cycle in the thread graph.');
    }
    const doc = (await deps.loadDoc(target.filePath));
    const updated = { ...doc, depends_on: [...input.dependsOn], updated: (0, dist_1.today)() };
    await deps.saveDoc(updated, target.filePath);
    return { id: doc.id, filePath: target.filePath, dependsOn: input.dependsOn };
}
